/// <amd-module name="DistributedTaskUI/Logs/ExpandedLogsTaskHeader" />
import * as React from "react";

import * as Resources from "DistributedTaskUI/Logs/Resources";
import { ITaskLog, IssueType } from "DistributedTaskUI/Logs/Logs.Types";
import { LogsViewUtility } from "DistributedTaskUI/Logs/LogsViewUtility";

import { Button } from "VSSUI/Button";
import { DefaultPalette } from "OfficeFabric/Styling";
import { Toggle } from "VSSUI/Toggle";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";

import { IVssPageContext } from "VSS/Platform/Context";
import { IVssThemeService } from "VSS/Platform/Theming";
import { VssIcon, VssIconType } from "VSSUI/VssIcon";
import { PickListDropdown, IPickListDropdownProps } from "VSSUI/PickList";
import { Status, StatusSize } from "VSSUI/Status";

import "VSS/LoaderPlugins/Css!DistributedTaskUI/Logs/ExpandedLogsTaskHeader";

export interface IExpandedLogsTaskHeaderProps {
    task: ITaskLog;
    logUrl: string | undefined;
    onCloseClick: () => void;
    isLive?: boolean;
    nextTaskButtonEnabled?: boolean;
    previousTaskButtonEnabled?: boolean;
    nextTaskDisplayText?: string;
    previousTaskDisplayText?: string;
    onNextTaskButtonClicked?: () => void;
    onPreviousTaskButtonClicked?: () => void;
    onFollowTailChange?: (value: boolean) => void;
    picklistDropdownProps?: IPickListDropdownProps;
    onHeadingTextKeyDown?: (evt: React.KeyboardEvent<HTMLElement>) => void;
    pageContext?: IVssPageContext;
}

export interface IExpandedLogsTaskHeaderState {
    followTail: boolean;
}

const LogsViewUtility2 = LogsViewUtility; // This is for decreasing size of the minified script
let whiteColor: string = DefaultPalette.white;

export class ExpandedLogsTaskHeader extends React.Component<IExpandedLogsTaskHeaderProps, IExpandedLogsTaskHeaderState> {

    constructor(props: IExpandedLogsTaskHeaderProps) {
        super(props);
        this.state = {
            followTail: !!this.props.isLive
        } as IExpandedLogsTaskHeaderState;

        // This is to handle theming issue which occur with hardcoded color
        const themeService = this.props.pageContext ? this.props.pageContext.getService<IVssThemeService>("IVssThemeService") : null;
        if (themeService) {
            whiteColor = `rgba(${themeService.getThemeValue("palette-neutral-0")}, 1)`;
        }
    }

    public componentDidUpdate(prevProps: IExpandedLogsTaskHeaderProps): void {
        if (prevProps.isLive !== this.props.isLive) {
            this.setState({ followTail: !!this.props.isLive });
        }
    }

    public render(): React.ReactNode {
        const task = this.props.task;
        const errors = LogsViewUtility2.getIssuesWithType(IssueType.Error, task);
        const taskStatus = LogsViewUtility2.getTaskStatus(task.state, errors ? errors.length : 0);
        const iconClassName = "logs-expanded-view-task-status-icon";
        return (
            <div className="logs-task-expanded-view-header flex" role="region" aria-label={Resources.LogsComponentHeaderRegionAriaLabel}>
                <div className="logs-view-header-left-section flex">
                    <div className="header-title-container flex">
                        {
                            taskStatus.statusProps && (
                                <Status {...taskStatus.statusProps} size={StatusSize.m} className={iconClassName} />
                            )
                        }
                        {
                            !taskStatus.statusProps && (
                                <VssIcon
                                    className={css(iconClassName, "log-task-state", taskStatus.className)}
                                    iconName={taskStatus.iconName}
                                    iconType={VssIconType.fabric}
                                />
                            )
                        }

                        <div className={"header-title-container-tooltip-host"}>
                            <Tooltip overflowOnly={true} showOnFocus={true} text={task.name}>
                                <div role="heading" aria-level={1} className="logs-task-name ellipsis-overflow" onKeyDown={this._onkeyDown} data-is-focusable={true}>{task.name}</div>
                            </Tooltip>
                        </div>
                    </div>
                </div>
                <div className="logs-view-header-right-section">
                    {(this.props.picklistDropdownProps) ? <PickListDropdown className="logs-header-picklist-title flex" {...this.props.picklistDropdownProps} /> : null}
                    {!this.props.picklistDropdownProps && this._getLogsViewActionSection()}
                    <div className="logs-header-separator" />
                    {this._getCloseViewButton()}
                </div>
            </div>
        );
    }

    private _onkeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
        if (this.props.onHeadingTextKeyDown) {
            this.props.onHeadingTextKeyDown(evt);
        }
    }

    private _getLogsViewActionSection(): JSX.Element {
        const showNavigationButtons: boolean = this._isAnyNavigationButtonEnabled();
        const previousButtonText: string = this.props.previousTaskDisplayText ? this.props.previousTaskDisplayText : Resources.PreviousTaskButtonText;
        const nextButtonText: string = this.props.nextTaskDisplayText ? this.props.nextTaskDisplayText : Resources.NextTaskButtonText;

        return (
            <div className="logs-view-actions-section flex">
                {this._getFollowTailToggle()}
                {showNavigationButtons && this._getNavigationButton("Up", previousButtonText, !this.props.previousTaskButtonEnabled, this._onPreviousTaskNavigate)}
                {showNavigationButtons && this._getNavigationButton("Down", nextButtonText, !this.props.nextTaskButtonEnabled, this._onNextTaskNavigate)}
            </div>
        );
    }

    private _isAnyNavigationButtonEnabled(): boolean {
        return !!this.props.nextTaskButtonEnabled || !!this.props.previousTaskButtonEnabled;
    }

    private _getNavigationButton(iconName: string, text: string, disabled: boolean, onClick: () => void) {
        return (
            <div className="navigate-task-button">
                {this._getButton(text, iconName, disabled, onClick)}
            </div>
        );
    }

    private _getFollowTailToggle(): React.ReactNode {
        if (this.props.isLive) {
            return (
                <div className="follow-tail-toggle">
                    <Toggle
                        checked={this.state.followTail}
                        onText={Resources.LogComponentFollowTailToggleLabel}
                        onAriaLabel={Resources.FollowTailEnabled}
                        offText={Resources.LogComponentFollowTailToggleLabel}
                        offAriaLabel={Resources.FollowTailDisabled}
                        onChange={this._toggleFollowTail}
                    />
                </div>
            );
        } else {
            return null;
        }
    }

    private _getButton(label: string, icon: string, disabled: boolean, onClick: (event: React.MouseEvent<HTMLButtonElement>) => void): JSX.Element {
        let className: string = "logs-view-action-button";
        if (disabled) {
            className = "logs-view-action-button logs-view-action-button-disabled";
        }
        return (
            <Button
                subtle={true}
                className={className}
                iconProps={{ className: "logs-view-action-button-icon", iconName: icon }}
                ariaLabel={label}
                disabled={disabled}
                onClick={onClick}>
                {label}
            </Button>
        );
    }

    private _toggleFollowTail = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, followTail: boolean): void => {
        this.setState({ followTail: followTail });
        if (this.props.onFollowTailChange) {
            this.props.onFollowTailChange(followTail);
        }
    }

    private _onNextTaskNavigate = (): void => {
        if (this.props.onNextTaskButtonClicked) {
            this.props.onNextTaskButtonClicked();
        }
    }

    private _onPreviousTaskNavigate = (): void => {
        if (this.props.onPreviousTaskButtonClicked) {
            this.props.onPreviousTaskButtonClicked();
        }
    }

    private _getCloseViewButton(): JSX.Element {
        return (
            <Button
                subtle={true}
                ariaLabel={Resources.TaskLogExpandedViewCloseButtonAriaLabel}
                className={"logs-expanded-view-close-button"}
                iconProps={{ className: "bowtie-icon bowtie-navigate-close logs-expanded-view-close-icon" }}
                onClick={this._onCloseButtonClick}
                disabled={false} />
        );
    }

    private _onCloseButtonClick = (): void => {
        if (this.props.onCloseClick) {
            this.props.onCloseClick();
        }
    }
}
